/* eslint-disable @typescript-eslint/no-explicit-any */
import isNetworkError from 'is-network-error';
import retry, { type FailedAttemptError } from 'p-retry';

import { ResourceAbort } from './aborts/resourceAborts';
import { type File } from './types/file';
import { useUserTokensStore } from '../../stores/userTokensStore/userTokensStore';
import { UserService } from '../user/userService';

interface FindResourcesPayload {
  page: number;
  pageSize: number;
  abortSignal: AbortSignal;
}

interface DownloadResourcesPayload {
  names: string[];
}

interface UploadResourcesPayload {
  files: File[];
  manualAbort?: AbortSignal;
}

export class ResourceService {
  private static apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? 'api';

  public static async findResources(payload: FindResourcesPayload): Promise<any> {
    const { abortSignal, page, pageSize } = payload;

    const urlSearchParams = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    const userStore = useUserTokensStore();

    const result = await retry(
      async () => {
        const response = await fetch(`${this.apiBaseUrl}/resources?` + urlSearchParams, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userStore.getLoggedInUserAccessToken}`,
          },
          signal: abortSignal,
        });

        const success = response.status >= 200 && response.status < 300;

        const responseBody = await response.json();

        if (!success) {
          const message = responseBody.error?.message ?? responseBody.message;

          await this.handleError(response.status, message);
        }

        return responseBody;
      },
      {
        retries: 2,
        signal: abortSignal,
        onFailedAttempt: (error) => {
          if (Number(error.message) === 404 || Number(error.message) === 500) {
            throw error;
          }
        },
      },
    );

    return result;
  }

  public static async downloadImage(payload: any): Promise<Blob | null> {
    const { name, abort } = payload;

    const result = await retry(
      async () => {
        const userStore = useUserTokensStore();

        const response = await fetch(`${this.apiBaseUrl}/resources/${name}`, {
          method: 'GET',
          headers: {
            Accept: 'image/*',
            Authorization: `Bearer ${userStore.getLoggedInUserAccessToken}`,
          },
          signal: abort,
        });

        const success = response.status === 200;

        if (!success) {
          const responseJson = await response.json();
          const message = responseJson.error?.message ?? responseJson.message;
          if (response.status === 404) {
            console.error(message);
            return null;
          }

          await this.handleError(response.status, message);
        }

        const responseBody = await response.blob();

        return responseBody;
      },
      {
        retries: 2,
        signal: abort,
      },
    );

    return result;
  }

  public static async downloadResource(payload: any): Promise<Blob> {
    const { name } = payload;

    const abortController = new AbortController();

    const result = await retry(
      async () => {
        const userStore = useUserTokensStore();

        const response = await fetch(`${this.apiBaseUrl}/resources/${name}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userStore.getLoggedInUserAccessToken}`,
          },
          signal: abortController.signal,
        });

        const success = response.status === 200;

        if (!success) {
          const responseJson = await response.json();
          const message = responseJson.error?.message ?? responseJson.message;

          await this.handleError(response.status, message);
        }

        const responseBody = await response.blob();

        return responseBody;
      },
      {
        retries: 2,
        onFailedAttempt: (error) => {
          if (error.attemptNumber === 2) {
            abortController.abort();
          }
        },
      },
    );

    return result;
  }

  public static async deleteResource(payload: any): Promise<void> {
    const { name } = payload;

    const userStore = useUserTokensStore();

    const result = await retry(
      async () => {
        const response = await fetch(`${this.apiBaseUrl}/resources/${name}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${userStore.getLoggedInUserAccessToken}`,
          },
        });

        const success = response.status >= 200 && response.status < 300;

        if (!success) {
          const responseBody = await response.json();
          const message = responseBody.error?.message ?? responseBody.message;

          await this.handleError(response.status, message);
        }
      },
      {
        retries: 2,
        shouldRetry: (error) => {
          return error.attemptNumber < 2;
        },
      },
    );

    return result;
  }

  public static async downloadResources(payload: DownloadResourcesPayload = { names: [] }): Promise<string> {
    const { names } = payload;

    const abortController = new AbortController();

    const result = await retry(
      async () => {
        const userStore = useUserTokensStore();

        const response = await fetch(`${this.apiBaseUrl}/resources/export`, {
          method: 'POST',
          body: JSON.stringify({
            names,
          }),
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userStore.getLoggedInUserAccessToken}`,
          },
          signal: abortController.signal,
        });

        const success = response.status === 200;

        if (!success) {
          const responseJson = await response.json();
          const message = responseJson.error?.message ?? responseJson.message;

          await this.handleError(response.status, message);
        }

        const responseBody = await response.json();

        return responseBody;
      },
      {
        retries: 2,
        onFailedAttempt: (error) => {
          console.log({ error });

          if (error.message !== 'Token expired. Retrying...') {
            abortController.abort();
          }
        },

        signal: abortController.signal,
      },
    );

    const resultUrl = result.token;

    return resultUrl;
  }

  public static async getResourcesURL(): Promise<URL> {
    const userStore = useUserTokensStore();

    const result = new URL(`${this.apiBaseUrl}/resources/export`);

    result.searchParams.append('token', userStore.getLoggedInUserAccessToken ?? '');

    return result;
  }

  public static async uploadResource(payload: UploadResourcesPayload): Promise<void> {
    const { files, manualAbort } = payload;

    const userStore = useUserTokensStore();

    const formData = new FormData();

    for (const file of files) {
      formData.append('attachedFiles', file as unknown as Blob, file.name);
    }

    const abortController = new AbortController();

    manualAbort?.addEventListener('abort', () => {
      abortController.abort();
    });

    const timeout = setTimeout(() => {
      abortController.abort(ResourceAbort.timeout);
    }, 180000);

    const result = await retry(
      async () => {
        const response = await fetch(`${this.apiBaseUrl}/resources`, {
          method: 'POST',
          body: formData,
          headers: {
            Authorization: `Bearer ${userStore.getLoggedInUserAccessToken}`,
          },
          signal: abortController.signal,
        });

        const success = response.status >= 200 && response.status < 300;

        if (!success) {
          const responseBody = await response.json();
          const message = responseBody.error?.message ?? responseBody.message;

          await this.handleError(response.status, message);
        }
      },
      {
        retries: 2,
        shouldRetry: (error) => {
          const errorStatus = Number(error.message);

          if (isNetworkError(error)) {
            abortController.abort(ResourceAbort.network);

            return false;
          }

          if (errorStatus === 500 || errorStatus === 400 || errorStatus === 404 || errorStatus === 401) {
            return false;
          } else if (Number((error as FailedAttemptError).message) === 404) {
            return false;
          }

          return (error as FailedAttemptError).attemptNumber < 2;
        },
        signal: abortController.signal,
      },
    );

    clearTimeout(timeout);

    return result;
  }

  private static async handleError(status: number, _: string): Promise<void> {
    if (status === 401) {
      const userStore = useUserTokensStore();

      if (userStore.getLoggedInUserRefreshToken) {
        const response = await UserService.refreshToken({ refreshToken: userStore.getLoggedInUserRefreshToken });

        const { accessToken, expiresIn, refreshToken: newRefreshToken } = response;

        userStore.setUserTokens({
          accessToken,
          refreshToken: newRefreshToken,
          expiresIn,
        });

        throw new Error('Token expired. Retrying...');
      }
    } else {
      throw new Error(`${status}`);
    }
  }
}
